import {
  Box,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { commonStyle } from "../common.styles";
import TransactionIcon from "assets/svg/TransactionIcon";
import {
  DataGrid,
  GridRenderCellParams,
  GridRowParams,
  useGridApiRef,
} from "@mui/x-data-grid";
import { transactionColumn } from "./dataGridDef";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "reduxStore/store";
import { useEffect, useState } from "react";
import { FETCH_TRANSACTIONS } from "reduxStore/actions/authActionType";
import NoRowsTableDataOverlay from "components/NoRowsTableDataOverlay/NoRowsTableDataOverlay";

const TransactionsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { transactions, loading } = useSelector(
    (state: RootState) => state.transaction
  );
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const statusOptions = ["Completed", "Pending", "Failed"];
  const typeOptions = ["Transfer", "Receive"];

  const [filters, setFilters] = useState({
    status: "",
    type: "",
    from_date: null as Dayjs | null,
    to_date: null as Dayjs | null,
  });

  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<any[]>([]);

  useEffect(() => {
    // Fetch all transactions once for client-side filtering
    dispatch({ type: FETCH_TRANSACTIONS, payload: { page: 1, limit: 1000 } });
  }, [dispatch]);

  useEffect(() => {
    setAllTransactions(transactions?.data?.transactions || []);
  }, [transactions]);

  useEffect(() => {
    let txs = allTransactions;

    if (filters.status) {
      txs = txs.filter(
        (tx: any) =>
          (tx.status || "").toLowerCase() === filters.status.toLowerCase()
      );
    }
    if (filters.type) {
      txs = txs.filter(
        (tx: any) =>
          (tx.txType || "").toLowerCase() === filters.type.toLowerCase()
      );
    }
    if (filters.from_date) {
      txs = txs.filter((tx: any) => {
        if (!tx.createdAt || !filters.from_date) return false;
        return dayjs(tx.createdAt).isAfter(
          filters.from_date.subtract(1, "day")
        );
      });
    }
    if (filters.to_date) {
      txs = txs.filter((tx: any) => {
        if (!tx.createdAt || !filters.to_date) return false;
        return dayjs(tx.createdAt).isBefore(filters.to_date.add(1, "day"));
      });
    }
    setFilteredTransactions(txs);
  }, [filters, allTransactions]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page on filter change
  };

  const handleClearAll = () => {
    setFilters({ status: "", type: "", from_date: null, to_date: null });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/dashboard/transactions/${params.row.transactionId}`);
  };

  const updatedColumns = transactionColumn.map((col) =>
    col.field === "id"
      ? {
          ...col,
          renderCell: (params: GridRenderCellParams) => {
            const { id } = params;
            const rowIndex = params.api.getRowIndexRelativeToVisibleRows(id);
            const page = apiRef.current.state.pagination.paginationModel.page;
            const pageSize =
              apiRef.current.state.pagination.paginationModel.pageSize;
            return rowIndex + 1 + page * pageSize;
          },
        }
      : col
  );

  return (
    <Box sx={{ ...commonStyle.cardStyle, flex: 1 }}>
      <Stack direction={"row"} alignItems={"center"} gap={1} mb={4}>
        <TransactionIcon width={22} height={22} />
        <Typography sx={{ ...commonStyle.cardTitleText, paddingBottom: 0.5 }}>
          Transactions
        </Typography>
      </Stack>
      {/* Filter Bar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          justifyContent: "flex-end",
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        {/* Status Dropdown */}
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            displayEmpty
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            sx={{ color: filters.status ? "inherit" : "#b0b0b0", fontSize: 13 }}
          >
            <MenuItem
              value=""
              disabled
              style={{ color: "#b0b0b0", fontSize: 13 }}
            >
              Status
            </MenuItem>
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status} sx={{ fontSize: 13 }}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Type Dropdown */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            displayEmpty
            value={filters.type}
            onChange={(e) => handleFilterChange("type", e.target.value)}
            sx={{ color: filters.type ? "inherit" : "#b0b0b0", fontSize: 13 }}
          >
            <MenuItem
              value=""
              disabled
              style={{ color: "#b0b0b0", fontSize: 13 }}
            >
              Transaction Type
            </MenuItem>
            {typeOptions.map((type) => (
              <MenuItem key={type} value={type} sx={{ fontSize: 13 }}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Date Pickers */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="From Date"
            value={filters.from_date}
            onChange={(newValue) => handleFilterChange("from_date", newValue)}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  maxWidth: 150,
                  "& .MuiInputBase-input": { fontSize: 13 },
                },
                InputLabelProps: { shrink: true, sx: { fontSize: 13 } },
                placeholder: "From Date",
              },
            }}
            maxDate={filters.to_date || undefined}
          />
          <DatePicker
            label="To Date"
            value={filters.to_date}
            onChange={(newValue) => handleFilterChange("to_date", newValue)}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  maxWidth: 150,
                  "& .MuiInputBase-input": { fontSize: 13 },
                },
                InputLabelProps: { shrink: true, sx: { fontSize: 13 } },
                placeholder: "To Date",
              },
            }}
            minDate={filters.from_date || undefined}
          />
        </LocalizationProvider>
        {/* Clear All Button */}
        <Box
          sx={{
            width: { xs: "100%", sm: "auto" },
            display: "flex",
            justifyContent: { xs: "flex-end", sm: "unset" },
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearAll}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Clear All
          </Button>
        </Box>
      </Box>
      {/* DataGrid */}
      <DataGrid
        apiRef={apiRef}
        rows={filteredTransactions} // Changed from transactions?.data?.transactions
        columns={updatedColumns}
        getRowId={(row) => row.transactionId}
        onRowClick={handleRowClick}
        loading={loading}
        slots={{
          noRowsOverlay: () => (
            <NoRowsTableDataOverlay title={"No transactions found"} />
          ),
        }}
        sx={{
          border: 0,
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
          },
        }}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        rowCount={filteredTransactions.length} // Changed from transactions?.data?.totalCount
        paginationMode="client" // Changed from "server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10]}
        disableRowSelectionOnClick
      />
    </Box>
  );
};
export default TransactionsPage;
